import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import LoginPage from "@/pages/Login";
import SignupPage from "@/pages/Signup";
import VerifyEmailPage from "@/pages/VerifyEmail";
import AuthCallback from "@/pages/AuthCallback";
import DashboardPage from "@/pages/Dashboard";
import ProjectRegistryPage from "@/pages/ProjectRegistry";
import CreateProjectPage from "@/pages/CreateProject";
import ProjectDetailPage from "@/pages/ProjectDetail";
import InboxPage from "@/pages/Inbox";
import AdminNumberingPage from "@/pages/AdminNumbering";
import MyTasksPage from "@/pages/MyTasks";
import MyDayPage from "@/pages/MyDay";
import OrderRequestsPage from "@/pages/OrderRequests";
import AdminUsersPage from "@/pages/AdminUsers";
import { AdminCustomersPage, AdminSuppliersPage } from "@/pages/AdminMasterData";
import AdminWorkflowPage from "@/pages/AdminWorkflow";
import AdminEconomicFilePage from "@/pages/AdminEconomicFile";
import AdminBackupPage from "@/pages/AdminBackup";
import AdminWorkspaceSettingsPage from "@/pages/AdminWorkspaceSettings";
import NotificationSettingsPage from "@/pages/NotificationSettings";
import MyAccountPage from "@/pages/MyAccount";
import ContactFeedbackPage from "@/pages/ContactFeedback";
import PlatformAdminPage from "@/pages/PlatformAdmin";
import AcceptInvitePage from "@/pages/AcceptInvite";
import ResetPasswordPage from "@/pages/ResetPassword";
import ArchivePage from "@/pages/Archive";
import LegalPage from "@/pages/LegalPage";
import AppLayout from "@/components/AppLayout";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-300 border-t-slate-900" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/privacy" element={<LegalPage kind="privacy" />} />
      <Route path="/terms" element={<LegalPage kind="terms" />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="projects" element={<ProjectRegistryPage />} />
        <Route path="projects/new" element={<CreateProjectPage />} />
        <Route path="projects/:id" element={<ProjectDetailPage />} />
        <Route path="archive" element={<ArchivePage />} />
        <Route path="inbox" element={<InboxPage />} />
        <Route path="tasks" element={<MyTasksPage />} />
        <Route path="my-day" element={<MyDayPage />} />
        <Route path="account" element={<MyAccountPage />} />
        <Route path="contact" element={<ContactFeedbackPage />} />
        <Route path="orders" element={<OrderRequestsPage />} />
        <Route path="settings/notifications" element={<NotificationSettingsPage />} />
        <Route path="admin/numbering" element={<AdminNumberingPage />} />
        <Route path="admin/users" element={<AdminUsersPage />} />
        <Route path="admin/customers" element={<AdminCustomersPage />} />
        <Route path="admin/suppliers" element={<AdminSuppliersPage />} />
        <Route path="admin/workflow" element={<AdminWorkflowPage />} />
        <Route path="admin/economic-file" element={<AdminEconomicFilePage />} />
        <Route path="admin/backup" element={<AdminBackupPage />} />
        <Route path="admin/settings" element={<AdminWorkspaceSettingsPage />} />
        <Route path="platform" element={<PlatformAdminPage />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
